import React, { useRef, useState } from "react"

import { ReactComponent as IconCopy } from "@common_assets/svg/copy.svg"
import { ReactComponent as IconMonitorUpArrow } from "@common_assets/svg/monitor-up-arrow.svg"
import { Box, Typography, Tooltip, useTheme } from "@mui/material"
import DialogClassCode from "components/module/classStudentsTeacher/common/viewCommonElementsContainer/dialogClassCode"
import { useDialog } from "hooks/dialog"
import { useClassId } from "hooks/navigation"
import { useGetQueryData } from "hooks/reactQuery"
import { useCustomSnackbar } from "hooks/snackbar"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { IClass } from "ts/interfaces/Class"

interface IProps {
  variant?: "compact" | "full"
}

const ClassCodeButton: React.FC<IProps> = ({ variant = "compact" }) => {
  const { t } = useTranslation()
  const classId = useClassId()
  const [isCopied, setIsCopied] = useState(false)
  const [showIcon, setShowIcon] = useState(false)
  const { showSnackbar } = useCustomSnackbar()
  const theme = useTheme()
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const {
    isOpen: isDialogOpen,
    handleOpen: handleOpenDialog,
    handleClose: handleCloseDialog,
  } = useDialog()

  const handleOpenIconDialog = () => {
    handleOpenDialog()
    mixpanel.track("add_students", {
      event_name: "class_code",
      action: "open",
    })
  }

  const classData = useGetQueryData<IClass>(["classes", Number(classId)])
  const classCode = classData?.data.invitation_code

  const handleCopy = async () => {
    if (!classCode) return
    try {
      await navigator.clipboard.writeText(classCode)
      setIsCopied(true)
      setTimeout(() => {
        setIsCopied(false)
      }, 1500)

      setShowIcon(true)
      setTimeout(() => {
        setShowIcon(false)
      }, 3000)
    } catch {
      showSnackbar({
        title: t("students.failedToCopyClassCode"),
        variant: "error",
      })
    }
  }

  if (!classCode) return null

  return (
    <>
      <Box sx={{ position: "relative" }}>
        {showIcon && (
          <Box
            component="button"
            type="button"
            onClick={handleOpenIconDialog}
            aria-label={t("students.clickToShowClassCode")}
            sx={{
              position: "absolute",
              top: "-16px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "32px",
              height: "32px",
              border: "2px solid",
              borderColor: "primary.main",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              zIndex: 1,
              backgroundColor: "common.white",
              fontFamily: "inherit",
              padding: 0,
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            }}
          >
            <IconMonitorUpArrow
              style={{
                width: "18px",
                height: "18px",
                color: theme.palette.primary.main,
              }}
            />
          </Box>
        )}

        <Tooltip
          title={t("students.classCodeCopied")}
          open={isCopied}
          placement="top"
          disableFocusListener
          disableHoverListener
          disableTouchListener
          PopperProps={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 4],
                },
              },
            ],
          }}
        >
          <Box
            ref={buttonRef}
            component="button"
            type="button"
            onClick={handleCopy}
            aria-label={t("students.copyClassCode")}
            sx={{
              cursor: "pointer",
              border: (theme) => `1px solid ${theme.palette.primary.main}`,
              borderRadius: "8px",
              paddingX: variant === "compact" ? 1.5 : 2,
              paddingY: 0,
              backgroundColor: "common.white",
              transition: "all 0.2s ease",
              fontFamily: "inherit",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "primary.light",
              },
              display: "flex",
              alignItems: "center",
              gap: "8px",
              minWidth: variant === "compact" ? "auto" : "120px",
              maxWidth: { mobile: "90vw", tablet: "none" },
              margin: { mobile: "0 auto", tablet: 0 },
              overflowX: "auto",
              height: { mobile: "44px", tablet: "56px", desktop: "56px" },
              position: "relative",
            }}
          >
            <IconCopy
              style={{
                width: variant === "compact" ? "16px" : "20px",
                height: variant === "compact" ? "16px" : "20px",
                color: isCopied
                  ? theme.palette.success.main
                  : theme.palette.primary.main,
                transition: "color 0.2s ease",
                minWidth: "16px",
                minHeight: "16px",
              }}
            />
            <Typography
              component="span"
              variant={variant === "compact" ? "body2" : "body1"}
              color={isCopied ? theme.palette.success.main : "primary.main"}
              fontWeight="600"
              fontFamily="monospace"
              sx={{
                fontSize: {
                  mobile: variant === "compact" ? "14px" : "16px",
                  tablet: variant === "compact" ? "16px" : "18px",
                },
                whiteSpace: "nowrap",
                transition: "color 0.2s ease",
              }}
            >
              {classCode}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {isDialogOpen && (
        <DialogClassCode
          isOpen={isDialogOpen}
          handleClose={handleCloseDialog}
        />
      )}
    </>
  )
}

export default ClassCodeButton
